import { useLocalStorage } from 'react-use';

import { Accordion } from '@/components/Accordion/Accordion';
import { MagicWand01Icon } from '@/components/icons/MagicWand01Icon';
import { HorizontalCarousel } from '@/components/layout/HorizontalCarousel/HorizontalCarousel';
import { LOCAL_STORAGE_KEYS } from '@/constants/localStorageKeys';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { PresentationTemplateSelectionModal } from '@/modules/presentationTemplates/presentationTemplateSelectionModal/PresentationTemplateSelectionModal';
import { useTenantDetailsContext } from '@/modules/tenant/TenantDetailsContext/TenantDetailsContext';
import { LuminaryThemeProvider } from '@/styles/themes/LuminaryThemeProvider';
import { COLORS } from '@/styles/tokens/colors';
import { TenantProductTier } from '@/types/schema';

import { useClientOnboardingDeliverables } from './hooks/useClientOnboardingDeliverables';

interface ClientOnboardingSectionProps {
  householdId: string;
}

export function ClientOnboardingSection({
  householdId,
}: ClientOnboardingSectionProps) {
  const { tier } = useTenantDetailsContext();
  const canEditHousehold = useCanEditHouseholdData();

  const [accordionExpanded, setAccordionExpanded] = useLocalStorage(
    LOCAL_STORAGE_KEYS.DELIVERABLE_ACCORDION_EXPANDED,
    true
  );

  const {
    presentationTemplateModalOpen,
    setPresentationTemplateModalOpen,
    deliverableCards,
  } = useClientOnboardingDeliverables({
    householdId,
  });

  if (tier === TenantProductTier.Go || !canEditHousehold) {
    return null;
  }

  return (
    <LuminaryThemeProvider>
      <Accordion
        leftAdornment={<MagicWand01Icon color={COLORS.NAVY[300]} />}
        isDefaultExpanded={accordionExpanded}
        onChange={(expanded) => setAccordionExpanded(expanded)}
        variant="filled"
        title="Get started with your next deliverable"
        withDivider={false}
      >
        <HorizontalCarousel
          items={deliverableCards}
          numVisibleItems={4}
          gap={16}
        />
      </Accordion>

      {presentationTemplateModalOpen && (
        <PresentationTemplateSelectionModal
          householdId={householdId}
          onClose={() => setPresentationTemplateModalOpen(false)}
        />
      )}
    </LuminaryThemeProvider>
  );
}
